import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

function buildVariantSlug(baseSlug, variationId) {
  if (!baseSlug || !variationId) return baseSlug;
  return `${baseSlug}-v-${variationId}`;
}

function normalizeConnectionLabel(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const lower = raw.toLowerCase().replaceAll("ё", "е");
  const compact = lower.replace(/\s+/g, "");
  const hasEsim = compact.includes("esim");
  const hasSim = /(^|[^a-z0-9])sim([^a-z0-9]|$)/.test(lower) || compact.includes("dualsim");

  if (compact.includes("cellular") || compact.includes("сотов") || compact.includes("lte") || compact.includes("5g")) {
    return "Wi-Fi + Cellular";
  }
  if (compact.includes("wi-fi") || compact.includes("wifi") || compact.includes("вайфай")) {
    if (compact.includes("cellular") || compact.includes("сотов") || compact.includes("lte") || compact.includes("5g")) {
      return "Wi-Fi + Cellular";
    }
    return "Wi-Fi";
  }
  if (compact.includes("sim+esim") || (hasSim && hasEsim)) {
    return "SIM + eSIM";
  }
  if (hasEsim) return "eSIM";
  if (hasSim) return "SIM";
  return raw;
}

function getProductConnection(product) {
  const specs = product?.specs || {};
  const specValue = specs["Связь"] || specs["SIM"] || specs["Тип SIM"] || specs["Сим-карта"] || specs["SIM-карта"];
  const fromSpecs = normalizeConnectionLabel(specValue);
  if (fromSpecs) return fromSpecs;
  return normalizeConnectionLabel(product?.name);
}

function getModelFilterLabel(product) {
  // Some categories (iPhone) bake storage into the model field ("iPhone 17
  // Pro Max 256Gb"), which used to make the "Модель" filter list one entry
  // per storage size instead of one per real model line. Strip just the
  // storage token; everything else (including a chip-variant marker like
  // "(M3 Ultra)" for Mac) stays, so those stay distinguishable.
  const raw = (product?.model || product?.name || "").replace(/^смартфон\s+/i, "").trim();
  return raw.replace(/\b\d+\s?(?:gb|гб|tb|тб)\b/gi, "").replace(/\s+/g, " ").trim();
}

function normalizeTextPart(value) {
  return (value || "")
    .toLowerCase()
    .replaceAll("ё", "е")
    .replace(/[^a-zа-я0-9]+/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function buildIphoneCardName(product, variation) {
  const baseRaw = (product.model || product.name || "").replace(/^смартфон\s+/i, "").trim();
  const baseNormalized = normalizeTextPart(baseRaw);

  const attributes = [variation.color, variation.memory || variation.storage, variation.region]
    .map((x) => String(x || "").trim())
    .filter(Boolean)
    .filter((part) => {
      const normalizedPart = normalizeTextPart(part);
      return normalizedPart && !baseNormalized.includes(normalizedPart);
    });

  return [baseRaw, ...attributes.map((part) => `• ${part}`)].join(" ").trim();
}

const SORTS = [
  { v: "", label: "По умолчанию" },
  { v: "price_asc", label: "Цена ↑" },
  { v: "price_desc", label: "Цена ↓" },
  { v: "new", label: "Новинки" },
];

// Rendering every page number broke down completely for big categories (iPad
// alone is 22 pages) - a wall of buttons that either overflows or wraps into
// a mess on mobile. This keeps first/last + a window around the current page,
// collapsing the rest into "…".
function buildPageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const keep = new Set([1, 2, total - 1, total, current - 1, current, current + 1]);
  const sorted = Array.from(keep).filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const withEllipsis = [];
  let prev = null;
  for (const p of sorted) {
    if (prev !== null && p - prev > 1) withEllipsis.push("…");
    withEllipsis.push(p);
    prev = p;
  }
  return withEllipsis;
}

export default function Catalog({ categoryMode = "path", basePath = "/catalog", heroLabel = "Каталог" }) {
  const { categorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1, page_size: 12 });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filterProducts, setFilterProducts] = useState([]);
  const [expandModels, setExpandModels] = useState(false);
  const [expandColors, setExpandColors] = useState(false);

  const page = parseInt(searchParams.get("page") || "1", 10);
  const q = searchParams.get("q") || "";
  const sort = searchParams.get("sort") || "";
  const color = searchParams.get("color") || "";
  const storage = searchParams.get("storage") || "";
  const region = searchParams.get("region") || "";
  const model = searchParams.get("model") || "";
  const material = searchParams.get("material") || "";
  const minPrice = searchParams.get("min_price") || "";
  const maxPrice = searchParams.get("max_price") || "";
  const [draftMinPrice, setDraftMinPrice] = useState(minPrice);
  const [draftMaxPrice, setDraftMaxPrice] = useState(maxPrice);
  const selectedCategory = categoryMode === "query" ? (searchParams.get("category") || "") : (categorySlug || "");

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === selectedCategory),
    [selectedCategory, categories]
  );
  const topLevelCategories = useMemo(() => categories.filter((c) => !c.parent_slug), [categories]);
  const parentCategory = useMemo(
    () => (currentCategory?.parent_slug ? categories.find((c) => c.slug === currentCategory.parent_slug) : null),
    [currentCategory, categories]
  );
  // Subcategory chips: children of the current parent, or - when a
  // subcategory itself is selected - its siblings under the same parent, so
  // the same switcher works whether you're on the umbrella page or already
  // inside one subcategory.
  const subcategories = useMemo(() => {
    const parentSlug = currentCategory?.parent_slug || selectedCategory;
    return categories.filter((c) => c.parent_slug === parentSlug);
  }, [categories, currentCategory, selectedCategory]);

  useEffect(() => {
    api.get("/catalog/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    const categoryName = currentCategory?.name;
    const title = q
      ? `«${q}» — поиск по vip77.store`
      : categoryName
        ? `${categoryName} — купить в vip77.store`
        : "Каталог — vip77.store";
    document.title = title;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        q
          ? `Результаты поиска «${q}» в каталоге vip77.store.`
          : categoryName
            ? `${categoryName}: оригинальная техника Apple с гарантией и доставкой по РФ. Каталог vip77.store.`
            : "Каталог оригинальной техники Apple — iPhone, Mac, iPad, Watch, AirPods. Гарантия и доставка по РФ."
      );
    }
  }, [currentCategory, q]);

  useEffect(() => {
    setLoading(true);
    const params = { page, page_size: 12 };
    if (selectedCategory) params.category = selectedCategory;
    if (q) params.q = q;
    if (sort) params.sort = sort;
    if (color) params.color = color;
    if (storage) params.storage = storage;
    if (region) params.region = region;
    if (model) params.model = model;
    if (material) params.material = material;
    if (minPrice) params.min_price = minPrice;
    if (maxPrice) params.max_price = maxPrice;
    api
      .get("/catalog/products", { params })
      .then((r) => {
        setProducts(r.data.items || []);
        setPagination({
          total: r.data.total,
          page: r.data.page,
          pages: r.data.pages,
          page_size: r.data.page_size,
        });
      })
      .finally(() => setLoading(false));
  }, [selectedCategory, q, sort, color, storage, region, model, material, minPrice, maxPrice, page]);

  useEffect(() => {
    const params = { limit: 500 };
    if (selectedCategory) params.category = selectedCategory;
    if (q) params.q = q;
    api
      .get("/catalog/products", { params })
      .then((r) => setFilterProducts(Array.isArray(r.data) ? r.data : []))
      .catch(() => setFilterProducts([]));
  }, [selectedCategory, q]);

  useEffect(() => {
    setExpandModels(false);
    setExpandColors(false);
  }, [selectedCategory]);

  useEffect(() => {
    setDraftMinPrice(minPrice);
    setDraftMaxPrice(maxPrice);
  }, [minPrice, maxPrice]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value) next.delete(key);
    else next.set(key, value);
    // reset to first page on any filter change (but not when the param is "page" itself)
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  };

  const gotoPage = (p) => {
    const next = new URLSearchParams(searchParams);
    if (p <= 1) next.delete("page");
    else next.set("page", String(p));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const activeFilterCount = [color, storage, region, model, material, minPrice, maxPrice].filter(Boolean).length;

  const clearFilters = () => {
    const next = new URLSearchParams();
    if (categoryMode === "query" && selectedCategory) {
      next.set("category", selectedCategory);
    }
    if (q) next.set("q", q);
    setSearchParams(next);
  };

  const applyPriceFilters = () => {
    const next = new URLSearchParams(searchParams);
    const minValue = (draftMinPrice || "").trim();
    const maxValue = (draftMaxPrice || "").trim();

    if (minValue) next.set("min_price", minValue);
    else next.delete("min_price");

    if (maxValue) next.set("max_price", maxValue);
    else next.delete("max_price");

    next.delete("page");
    setSearchParams(next);
  };

  // Extract unique filter values from products in this category
  const allColors = useMemo(() => {
    const s = new Map();
    const source = filterProducts.length ? filterProducts : products;
    source.forEach((p) =>
      (p.variations || []).forEach((v) => {
        const key = v.color?.toLowerCase();
        if (key && !s.has(key)) s.set(key, { name: v.color, hex: v.color_hex });
      })
    );
    return Array.from(s.values());
  }, [products, filterProducts]);

  const allStorages = useMemo(() => {
    const s = new Set();
    const source = filterProducts.length ? filterProducts : products;
    source.forEach((p) =>
      (p.variations || []).forEach((v) => (v.memory || v.storage) && s.add(v.memory || v.storage))
    );
    return Array.from(s);
  }, [products, filterProducts]);

  const allModels = useMemo(() => {
    const values = new Set();
    const source = filterProducts.length ? filterProducts : products;
    source.forEach((p) => {
      const v = getModelFilterLabel(p);
      if (v) values.add(v);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b, "ru"));
  }, [products, filterProducts]);

  const allMaterials = useMemo(() => {
    const values = new Set();
    const source = filterProducts.length ? filterProducts : products;
    source.forEach((p) => {
      const raw = (p.specs && p.specs["Материал"]) || "";
      if (raw) values.add(raw.trim());
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b, "ru"));
  }, [products, filterProducts]);

  const facetRows = useMemo(() => {
    const rows = [];
    const source = filterProducts.length ? filterProducts : products;
    source.forEach((p) => {
      const productModel = getModelFilterLabel(p);
      const productMaterial = ((p.specs && p.specs["Материал"]) || "").trim();
      const variations = p.variations || [];
      if (!variations.length) {
        rows.push({
          model: productModel,
          color: "",
          storage: "",
          region: "",
          material: productMaterial,
          price: Number(p.base_price || p.min_price || 0),
        });
      } else {
        variations.forEach((v) => {
          const regionValue = getProductConnection(p) || normalizeConnectionLabel(v.region) || "";
          rows.push({
            model: productModel,
            color: (v.color || "").trim(),
            storage: (v.memory || v.storage || "").trim(),
            region: regionValue,
            material: productMaterial,
            price: Number(v.price || p.base_price || p.min_price || 0),
          });
        });
      }
    });
    return rows;
  }, [products, filterProducts]);

  const allRegions = useMemo(() => {
    const values = new Set();
    facetRows.forEach((row) => {
      const value = (row.region || "").trim();
      if (value) values.add(value);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b, "ru"));
  }, [facetRows]);

  const makeFacetPredicate = (filters) => {
    const minP = filters.minPrice ? Number(filters.minPrice) : null;
    const maxP = filters.maxPrice ? Number(filters.maxPrice) : null;
    return (row) => {
      if (filters.color && row.color !== filters.color) return false;
      if (filters.storage && row.storage !== filters.storage) return false;
      if (filters.region && row.region !== filters.region) return false;
      if (filters.model && row.model !== filters.model) return false;
      if (filters.material && row.material !== filters.material) return false;
      if (minP !== null && row.price < minP) return false;
      if (maxP !== null && row.price > maxP) return false;
      return true;
    };
  };

  const hasMatchForFilters = (filters) => {
    const predicate = makeFacetPredicate(filters);
    return facetRows.some(predicate);
  };

  const facetFilterBase = useMemo(() => ({
    color,
    storage,
    region,
    model,
    material,
    minPrice,
    maxPrice,
  }), [color, storage, region, model, material, minPrice, maxPrice]);

  const getAvailableValueSet = (key) => {
    const scope = { ...facetFilterBase, [key]: "" };
    const predicate = makeFacetPredicate(scope);
    const values = new Set();
    for (const row of facetRows) {
      if (!predicate(row)) continue;
      const value = (row[key] || "").trim();
      if (value) values.add(value);
    }
    return values;
  };

  const availableModelSet = useMemo(() => getAvailableValueSet("model"), [facetRows, facetFilterBase]);
  const availableColorSet = useMemo(() => getAvailableValueSet("color"), [facetRows, facetFilterBase]);
  const availableStorageSet = useMemo(() => getAvailableValueSet("storage"), [facetRows, facetFilterBase]);
  const availableRegionSet = useMemo(() => getAvailableValueSet("region"), [facetRows, facetFilterBase]);
  const availableMaterialSet = useMemo(() => getAvailableValueSet("material"), [facetRows, facetFilterBase]);

  const modelOptions = useMemo(() => (
    allModels.filter((m) => availableModelSet.has(m) || model === m)
  ), [allModels, availableModelSet, model]);

  const materialOptions = useMemo(() => (
    allMaterials.filter((m) => availableMaterialSet.has(m) || material === m)
  ), [allMaterials, availableMaterialSet, material]);

  const colorOptions = useMemo(() => (
    allColors.filter((c) => availableColorSet.has(c.name) || color.toLowerCase() === c.name.toLowerCase())
  ), [allColors, availableColorSet, color]);

  const storageOptions = useMemo(() => (
    allStorages.filter((s) => availableStorageSet.has(s) || storage === s)
  ), [allStorages, availableStorageSet, storage]);

  const regionOptions = useMemo(() => (
    allRegions.filter((r) => availableRegionSet.has(r) || region === r)
  ), [allRegions, availableRegionSet, region]);

  const MODEL_VISIBLE_LIMIT = 8;
  const COLOR_VISIBLE_LIMIT = 10;
  const visibleModelOptions = expandModels ? modelOptions : modelOptions.slice(0, MODEL_VISIBLE_LIMIT);
  const visibleColorOptions = expandColors ? colorOptions : colorOptions.slice(0, COLOR_VISIBLE_LIMIT);

  const showStorageFilter = storageOptions.length > 0 && ["iphone", "ipad", "mac", "airpods", "apple-tv", "watch"].includes(selectedCategory);
  const storageFilterLabel = selectedCategory === "watch" ? "Размер" : "Память";
  const showRegionFilter = (regionOptions.length > 0 || !!region) && ["iphone", "ipad"].includes(selectedCategory);
  const showColorFilter = colorOptions.length > 0;
  const showMaterialFilter = materialOptions.length > 1 && selectedCategory === "watch";
  // Dyson's "model" values are really just product names (imported
  // accessories, brush sets, etc.), so on the umbrella "Dyson" page - which
  // aggregates every subcategory - the list is a huge, meaningless wall. Only
  // show it once a real subcategory narrows things down, and never for
  // Аксессуары Dyson, where there's no meaningful "model" concept at all.
  const isDysonTree = selectedCategory === "dyson" || currentCategory?.parent_slug === "dyson";
  const showModelFilter = isDysonTree
    ? selectedCategory !== "dyson" && selectedCategory !== "dyson-accessories" && (modelOptions.length > 1 || !!model)
    : modelOptions.length > 1 || !!model;

  const catalogItems = useMemo(() => {
    if (selectedCategory !== "iphone") return products;
    const out = [];
    for (const p of products) {
      const vars = p.variations || [];
      if (!vars.length) {
        out.push(p);
        continue;
      }
      for (const v of vars) {
        const cardName = buildIphoneCardName(p, v);
        out.push({
          ...p,
          card_key: `${p.id}:${v.id}`,
          urlSlug: buildVariantSlug(p.slug, v.id),
          card_name: cardName,
          card_image: (v.images || [])[0] || v.image || p.images?.[0],
          variations: [v],
          min_price: v.price,
          base_price: v.price,
        });
      }
    }
    return out;
  }, [products, selectedCategory]);

  const categoryLink = (slug) => {
    if (categoryMode === "query") {
      const next = new URLSearchParams(searchParams);
      next.set("category", slug);
      next.delete("page");
      return `${basePath}?${next.toString()}`;
    }
    return `${basePath}/${slug}`;
  };

  const filtersContent = (
    <div className="space-y-8">
      <div>
        <h3 className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-muted-soft">Категория</h3>
        <div className="flex flex-col gap-1">
          {topLevelCategories.map((c) => (
            <Link
              key={c.slug}
              to={categoryLink(c.slug)}
              data-testid={`filter-cat-${c.slug}`}
              className={`rounded-lg px-3 py-2 text-sm transition-colors ${
                c.slug === selectedCategory || c.slug === currentCategory?.parent_slug
                  ? "bg-ink font-semibold text-white"
                  : "text-ink hover:bg-black/5"
              }`}
            >
              {c.name}
            </Link>
          ))}
        </div>
      </div>

      {subcategories.length > 0 && (
        <div>
          <h3 className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-muted-soft">Подкатегория</h3>
          <div className="flex flex-wrap gap-2">
            {subcategories.map((c) => {
              const active = c.slug === selectedCategory;
              return (
                <Link
                  key={c.slug}
                  to={categoryLink(c.slug)}
                  data-testid={`filter-subcat-${c.slug}`}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    active ? "border-ink bg-ink text-white" : "border-line bg-white text-ink hover:border-ink"
                  }`}
                >
                  {c.name}
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {showModelFilter && (
        <div>
          <h3 className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-muted-soft">Модель</h3>
          <div className="flex flex-wrap gap-2">
            {visibleModelOptions.map((m) => {
              const active = model === m;
              return (
                <button
                  key={m}
                  data-testid={`filter-model-${m}`}
                  onClick={() => updateParam("model", active ? "" : m)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    active
                      ? "border-ink bg-ink text-white"
                      : "border-line bg-white text-ink hover:border-ink"
                  }`}
                >
                  {m}
                </button>
              );
            })}
          </div>
          {modelOptions.length > MODEL_VISIBLE_LIMIT && (
            <button
              type="button"
              onClick={() => setExpandModels((prev) => !prev)}
              className="mt-3 text-xs font-semibold text-ink underline-offset-2 hover:underline"
            >
              {expandModels ? "Свернуть" : `Показать ещё (${modelOptions.length - MODEL_VISIBLE_LIMIT})`}
            </button>
          )}
        </div>
      )}

      {showColorFilter && (
        <div>
          <h3 className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-muted-soft">Цвет</h3>
          <div className="flex flex-wrap gap-2">
            {visibleColorOptions.map((c) => {
              const active = color.toLowerCase() === c.name.toLowerCase();
              return (
                <button
                  key={c.name}
                  data-testid={`filter-color-${c.name}`}
                  onClick={() => updateParam("color", active ? "" : c.name)}
                  className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                    active
                      ? "border-ink bg-ink text-white"
                      : "border-line bg-white text-ink hover:border-ink"
                  }`}
                >
                  <span className="h-3 w-3 rounded-full ring-1 ring-line" style={{ background: c.hex }} />
                  {c.name}
                </button>
              );
            })}
          </div>
          {colorOptions.length > COLOR_VISIBLE_LIMIT && (
            <button
              type="button"
              onClick={() => setExpandColors((prev) => !prev)}
              className="mt-3 text-xs font-semibold text-ink underline-offset-2 hover:underline"
            >
              {expandColors ? "Свернуть" : `Показать ещё (${colorOptions.length - COLOR_VISIBLE_LIMIT})`}
            </button>
          )}
        </div>
      )}

      {showMaterialFilter && (
        <div>
          <h3 className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-muted-soft">Материал корпуса</h3>
          <div className="flex flex-wrap gap-2">
            {materialOptions.map((m) => {
              const active = material === m;
              return (
                <button
                  key={m}
                  data-testid={`filter-material-${m}`}
                  onClick={() => updateParam("material", active ? "" : m)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold capitalize transition-colors ${
                    active
                      ? "border-ink bg-ink text-white"
                      : "border-line bg-white text-ink hover:border-ink"
                  }`}
                >
                  {m}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {showStorageFilter && (
        <div>
          <h3 className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-muted-soft">{storageFilterLabel}</h3>
          <div className="flex flex-wrap gap-2">
            {storageOptions.map((s) => {
              const active = storage === s;
              return (
                <button
                  key={s}
                  data-testid={`filter-storage-${s}`}
                  onClick={() => updateParam("storage", active ? "" : s)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    active
                      ? "border-ink bg-ink text-white"
                      : "border-line bg-white text-ink hover:border-ink"
                  }`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {showRegionFilter && (
        <div>
          <h3 className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-muted-soft">Связь</h3>
          <div className="flex flex-wrap gap-2">
            {regionOptions.map((r) => {
              const active = region === r;
              return (
                <button
                  key={r}
                  data-testid={`filter-region-${r}`}
                  onClick={() => updateParam("region", active ? "" : r)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    active
                      ? "border-ink bg-ink text-white"
                      : "border-line bg-white text-ink hover:border-ink"
                  }`}
                >
                  {r}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <h3 className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-muted-soft">Цена, ₽</h3>
        <div className="flex items-center gap-2">
          <input
            data-testid="filter-min-price"
            type="number"
            placeholder="от"
            value={draftMinPrice}
            onChange={(e) => setDraftMinPrice(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyPriceFilters();
              }
            }}
            className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-ink"
          />
          <input
            data-testid="filter-max-price"
            type="number"
            placeholder="до"
            value={draftMaxPrice}
            onChange={(e) => setDraftMaxPrice(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyPriceFilters();
              }
            }}
            className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-ink"
          />
        </div>
        <button
          type="button"
          onClick={applyPriceFilters}
          className="mt-2 w-full rounded-full border border-line bg-white py-2 text-xs font-semibold text-ink transition-colors hover:border-ink"
        >
          Применить цену
        </button>
      </div>

      {(color || storage || region || model || material || minPrice || maxPrice) && (
        <button
          data-testid="filter-clear"
          onClick={clearFilters}
          className="w-full rounded-full border border-line bg-white py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white"
        >
          Сбросить фильтры
        </button>
      )}
    </div>
  );

  return (
    <div data-testid="catalog-page" className="mx-auto max-w-7xl px-4 py-10 lg:px-8">
      <div className="mb-6 flex items-center gap-2 text-xs text-muted">
        <Link to="/" className="hover:underline">Главная</Link>
        <span>/</span>
        {!q && parentCategory && (
          <>
            <Link to={categoryLink(parentCategory.slug)} className="hover:underline">{parentCategory.name}</Link>
            <span>/</span>
          </>
        )}
        <span className="text-ink">{q ? `Поиск: «${q}»` : currentCategory?.name || "Каталог"}</span>
      </div>

      <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-soft">{q ? "Результаты поиска" : heroLabel}</div>
          <h1 className="mt-1 text-4xl font-extrabold tracking-tightest text-ink md:text-5xl">
            {q ? `«${q}»` : currentCategory?.name || "Все товары"}
          </h1>
          <p className="mt-2 text-sm text-muted">
            {pagination.total} {pagination.total === 1 ? "товар" : "товаров"} в наличии
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="sort-select"
            value={sort}
            onChange={(e) => updateParam("sort", e.target.value)}
            className="rounded-full border border-line bg-white px-4 py-2 text-sm font-medium text-ink outline-none focus:border-ink"
          >
            {SORTS.map((s) => (
              <option key={s.v} value={s.v}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
        <aside className="hidden md:col-span-3 md:block">
          <div className="sticky top-24 rounded-2xl border border-line bg-white p-6">
            {filtersContent}
          </div>
        </aside>

        <div className="md:col-span-9">
          {loading ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="catalog-loading-skeleton">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="animate-pulse overflow-hidden rounded-2xl border border-line bg-white">
                  <div className="aspect-square bg-paper" />
                  <div className="space-y-3 p-5">
                    <div className="h-4 w-3/4 rounded-full bg-paper" />
                    <div className="h-3 w-1/2 rounded-full bg-paper" />
                    <div className="mt-2 flex gap-1.5">
                      <div className="h-4 w-4 rounded-full bg-paper" />
                      <div className="h-4 w-4 rounded-full bg-paper" />
                      <div className="h-4 w-4 rounded-full bg-paper" />
                    </div>
                    <div className="flex items-end justify-between pt-3">
                      <div className="h-5 w-20 rounded-full bg-paper" />
                      <div className="h-10 w-10 rounded-full bg-paper" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : catalogItems.length === 0 ? (
            <div className="rounded-2xl border border-line bg-white p-12 text-center">
              <h3 className="text-xl font-bold text-ink">Ничего не найдено</h3>
              <p className="mt-2 text-sm text-muted">Попробуйте изменить фильтры.</p>
              <button
                onClick={clearFilters}
                className="mt-6 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-ink-soft"
              >
                Сбросить фильтры
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {catalogItems.map((p) => (
                  <ProductCard key={p.card_key || p.id} product={p} />
                ))}
              </div>
              {pagination.pages > 1 && (
                <div data-testid="pagination">
                  {/* Mobile: compact prev/indicator/next - a wall of 20+ number
                      buttons has nowhere to go on a narrow screen. */}
                  <div className="mt-10 flex items-center justify-between gap-3 sm:hidden">
                    <button
                      data-testid="page-prev-mobile"
                      disabled={pagination.page <= 1}
                      onClick={() => gotoPage(pagination.page - 1)}
                      className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-line bg-white text-ink transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label="Назад"
                    >
                      <ChevronLeftIcon size={18} />
                    </button>
                    <span className="text-sm font-semibold text-muted">
                      Стр. {pagination.page} из {pagination.pages}
                    </span>
                    <button
                      data-testid="page-next-mobile"
                      disabled={pagination.page >= pagination.pages}
                      onClick={() => gotoPage(pagination.page + 1)}
                      className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-line bg-white text-ink transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label="Вперёд"
                    >
                      <ChevronRightIcon size={18} />
                    </button>
                  </div>

                  {/* Desktop: first/last + a window around the current page. */}
                  <div className="mt-10 hidden flex-wrap items-center justify-center gap-2 sm:flex">
                    <button
                      data-testid="page-prev"
                      disabled={pagination.page <= 1}
                      onClick={() => gotoPage(pagination.page - 1)}
                      className="rounded-full border border-line bg-white px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-ink hover:text-white disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-ink"
                    >
                      ← Назад
                    </button>
                    {buildPageList(pagination.page, pagination.pages).map((p, i) => (
                      p === "…" ? (
                        <span key={`ellipsis-${i}`} className="grid h-10 w-10 place-items-center text-sm text-muted-soft">…</span>
                      ) : (
                        <button
                          key={p}
                          data-testid={`page-${p}`}
                          onClick={() => gotoPage(p)}
                          className={`grid h-10 w-10 place-items-center rounded-full text-sm font-semibold transition-colors ${
                            p === pagination.page
                              ? "bg-ink text-white"
                              : "border border-line bg-white text-ink hover:bg-ink hover:text-white"
                          }`}
                        >
                          {p}
                        </button>
                      )
                    ))}
                    <button
                      data-testid="page-next"
                      disabled={pagination.page >= pagination.pages}
                      onClick={() => gotoPage(pagination.page + 1)}
                      className="rounded-full border border-line bg-white px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-ink hover:text-white disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-ink"
                    >
                      Вперёд →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Floating filters button - mobile only, stays reachable while
          scrolling the grid instead of a header button you have to scroll
          back up for. */}
      {!drawerOpen && (
        <button
          data-testid="filters-fab"
          onClick={() => setDrawerOpen(true)}
          className="fixed bottom-5 left-1/2 z-40 flex -translate-x-1/2 items-center gap-2 rounded-full bg-ink px-6 py-3.5 text-sm font-semibold text-white shadow-xl transition-transform active:scale-95 md:hidden"
        >
          <SlidersHorizontal size={16} />
          Фильтры
          {activeFilterCount > 0 && (
            <span className="grid h-5 min-w-[20px] place-items-center rounded-full bg-white px-1 text-[11px] font-bold text-ink">
              {activeFilterCount}
            </span>
          )}
        </button>
      )}

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="filters-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-4/5 max-w-sm overflow-y-auto bg-white p-6">
            <div className="mb-6 flex items-center justify-between">
              <div className="text-sm font-bold text-ink">Фильтры</div>
              <button onClick={() => setDrawerOpen(false)} className="grid h-8 w-8 place-items-center rounded-full hover:bg-black/5">
                <X size={16} />
              </button>
            </div>
            {filtersContent}
          </div>
        </div>
      )}
    </div>
  );
}
